import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from '../../firebase';
import useVoucherService from '../../services/VoucherService';


export interface LandingPageState {
    isSuccessState: boolean;
    isLoading: boolean;
    email: string;
    licenseCode: string;
    emailError: boolean;
    emailHelperText: string;
    voucherError: boolean;
    voucherHelpText: string;
    productKey: string | null;
    productKeyCopied: boolean;
    marketingConsent: boolean;
    recaptchaClientToken: string;
    companyLogo: string;
    recaptchaRefresh: boolean;
}

const initialState: LandingPageState = {
    isSuccessState: false,
    isLoading: false,
    email: "",
    licenseCode: "",
    emailError: false,
    emailHelperText: "",
    voucherError: false,
    voucherHelpText: "",
    productKey: null,
    productKeyCopied: false,
    marketingConsent: true,
    recaptchaClientToken: '',
    companyLogo: '',
    recaptchaRefresh: false,
}

export const LandingPageSlice = createSlice({
    name: 'landingPage',
    initialState,
    reducers: {
        onEmailChanged: (state, action: PayloadAction<string>) => {
            state.email = action.payload;
        },
        onLicenseCodeChanged: (state, action: PayloadAction<string>) => {
            let code = action.payload.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

            if (code == null) {
                return;
            }
            // Limit the length to 7 characters
            code = code.substring(0, 7);

            // Insert a ' - ' after every fourth character
            code = code.replace(/(.{4})/g, '$1 - ').trim();

            // Remove trailing dash if it exists
            if (code.endsWith(' -')) {
                code = code.slice(0, -2);
            }

            state.licenseCode = code;
        },
        setEmailError: (state, action: PayloadAction<boolean>) => {
            state.emailError = action.payload;
            state.emailHelperText = action.payload ? "Invalid Email" : "";
        },
        setLicenseCodeError: (state, action: PayloadAction<{ error: boolean; helperText: string }>) => {
            state.voucherError = action.payload.error;
            state.voucherHelpText = action.payload.helperText;
        },
        setLoadingState: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setSuccessState: (state, action: PayloadAction<boolean>) => {
            state.isSuccessState = action.payload;
        },
        setProductKey: (state, action: PayloadAction<string>) => {
            state.productKey = action.payload;
        },
        productKeyCopiedTapped: (state, action: PayloadAction) => {
            state.productKeyCopied = true;
        },
        marketingConsentOnChanged: (state, action: PayloadAction<boolean>) => {
            state.marketingConsent = action.payload;
        },
        recaptchaOnChanged: (state, action: PayloadAction<string>) => {
            state.recaptchaClientToken = action.payload;
        },
        updateCompanyLogo: (state, action: PayloadAction<string>) => {
            state.companyLogo = action.payload;
        },
        updateRecaptchaRefresh: (state, action: PayloadAction) => {
            state.recaptchaRefresh = !state.recaptchaRefresh;
        },
    }
});

export const submitAsync = createAsyncThunk<void, string, { state: RootState }>(
    'landingPage/submitAsync',
    async (token, { dispatch, getState, rejectWithValue }) => {
        let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        let appState = getState();
        const landingPageState = appState.landingPage;


        if (!re.test(landingPageState.email)) {
            dispatch(setEmailError(true));
            return;
        }
        dispatch(setEmailError(false));

        let code = landingPageState.licenseCode.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (code == null || code.length != 7) {
            dispatch(setLicenseCodeError({ error: true, helperText: "The license code must be 7 characters in length" }));
            return;
        }
        dispatch(setLicenseCodeError({ error: false, helperText: "" }));

        dispatch(setLoadingState(true));
        var voucherService = useVoucherService();
        var responseObject = await voucherService.validateAndAssignVoucherAsync(landingPageState.email, code, landingPageState.marketingConsent, token);

        if (responseObject['productKey'] != null && responseObject['productKey'] != undefined) {
            console.log("Success");
            dispatch(setProductKey(responseObject['productKey']))
            dispatch(setSuccessState(true));
            return;
        }

        dispatch(setLicenseCodeError({ error: true, helperText: responseObject['response'] }));
        dispatch(setLoadingState(false));
        return;
    }
);

export const {
    onEmailChanged,
    onLicenseCodeChanged,
    setEmailError,
    setLicenseCodeError,
    setLoadingState,
    setSuccessState,
    setProductKey,
    productKeyCopiedTapped,
    marketingConsentOnChanged,
    recaptchaOnChanged,
    updateCompanyLogo,
    updateRecaptchaRefresh,
} = LandingPageSlice.actions;

export const selectState = (state: RootState) => state.landingPage;

export default LandingPageSlice.reducer;